"use client";

import React from "react";
import { Droplet, Pipette, Search, ArrowRight, CircleAlert, Activity, Layers } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
const leakProblems = [{
  title: "Leaking Pipe",
  symptom: "Visible dripping, water stains, or damp drywall",
  description: "Pipes can crack, loosen at joints, or corrode over time. We isolate the damaged section quickly to prevent structural water damage and mold growth.",
  icon: "pipe"
}, {
  title: "Leaking Faucet",
  symptom: "Continuous dripping, sputtering, or low water pressure",
  description: "Worn O-rings, degraded valve seats, or high water pressure cause persistent faucet leaks that waste hundreds of gallons each week.",
  icon: "faucet"
}, {
  title: "Leaking Toilet",
  symptom: "Ghost flushing, water pooling around base, or whistling tank",
  description: "Faulty flappers, worn wax rings, or damaged fill valves can cause constant running water or hazardous subfloor moisture leaks.",
  icon: "toilet"
}, {
  title: "Water Leak Under Sink",
  symptom: "Puddles in cabinets, damp odors, or swollen cabinetry",
  description: "P-traps, loose slip joints, or faulty shutoff valves under kitchen and bathroom sinks frequently leak unnoticed until wood damage occurs.",
  icon: "sink"
}, {
  title: "Hidden Water Leak",
  symptom: "Unexplained high water bills, warm floor spots, or musty smells",
  description: "Slab leaks and concealed in-wall pipe ruptures require non-invasive acoustic and thermal detection to pinpoint without opening walls.",
  icon: "hidden"
}];
export default function WaterLeaks() {
  const getProblemIcon = (iconName: string) => {
    switch (iconName) {
      case "pipe":
        return <Activity className="size-6 text-primary" />;
      case "faucet":
        return <Droplet className="size-6 text-primary" />;
      case "toilet":
        return <Layers className="size-6 text-primary" />;
      case "sink":
        return <Pipette className="size-6 text-primary" />;
      case "hidden":
        return <Search className="size-6 text-primary" />;
      default:
        return <CircleAlert className="size-6 text-primary" />;
    }
  };
  return <section id="waterleakproblems" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 max-w-7xl">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-12 md:mb-16">
          <Badge variant="secondary" className="mb-3 font-medium">
            Diagnostic & Triage
          </Badge>
          <h2 className="font-default text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground mb-4">
            Water Leak Problems
          </h2>
          <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
            Fast triage for common residential leaks in Jonesboro. Identify your issue below to stop water damage before it spreads.
          </p>
        </div>

        {/* Problem Triage Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 items-stretch mb-12">
          {leakProblems.map((problem, i) => <div key={problem.title} data-index={i} className={i === 4 ? "md:col-span-2 lg:col-span-1" : ""}>
              <Card className="h-full border border-border/80 shadow-sm hover:shadow-md transition-[transform,box-shadow] hover:-translate-y-1 bg-card text-card-foreground rounded-xl flex flex-col justify-between">
                <div>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between mb-3">
                      <div className="size-12 rounded-lg bg-secondary flex items-center justify-center">
                        {getProblemIcon(problem.icon)}
                      </div>
                      <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        0{i + 1}
                      </span>
                    </div>
                    <CardTitle className="font-default text-xl font-bold text-card-foreground">
                      {problem.title}
                    </CardTitle>
                    <div className="mt-1">
                      <span className="inline-block text-xs font-medium text-destructive bg-destructive/10 px-2 py-0.5 rounded-sm">
                        {problem.symptom}
                      </span>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <CardDescription className="text-sm text-muted-foreground leading-normal">
                      {problem.description}
                    </CardDescription>
                  </CardContent>
                </div>
              </Card>
            </div>)}
        </div>

        {/* Section CTA */}
        <div className="flex justify-center items-center">
          <Button asChild size="lg" className="shadow-sm font-semibold group">
            <Link to="/leak-detection-repair/" className="inline-flex items-center gap-2">
              <span>View Water Leak Problems</span>
              <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}